import anime from "animejs"
import { BaseComponent, BElement } from "utils/base-component"
import "components/snow-icon"
import { Tabs } from "components/controls/tabs"
import { isMotionReduced } from "utils/motion-reduced"

import { Filter } from "./filter"

import "./index.scss"
import "./static/icon__cards.svg"
import "./static/icon__filter.svg"
import "./static/icon__list.svg"
import "./static/snow-finder.svg"

function openVideoModal(videoUrl: string) {
  const modal = document.createElement("div")
  modal.className = "video-modal"
  modal.innerHTML = `
    <div class="video-modal__overlay"></div>
    <div class="video-modal__content">
      <button class="video-modal__close" aria-label="Close video">×</button>
      <div class="video-modal__video-container">
        <iframe
          src="${videoUrl}"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowfullscreen
          class="video-modal__iframe">
        </iframe>
      </div>
    </div>
  `

  document.body.appendChild(modal)

  document.body.style.overflow = "hidden"

  const closeButton = modal.querySelector(".video-modal__close")
  const overlay = modal.querySelector(".video-modal__overlay")

  const closeModal = () => {
    modal.remove()
    document.body.style.overflow = ""
  }

  closeButton?.addEventListener("click", closeModal)
  overlay?.addEventListener("click", closeModal)

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      closeModal()
      document.removeEventListener("keydown", handleEscape)
    }
  }
  document.addEventListener("keydown", handleEscape)
}

type ResponseAirport = {
  code: string
  name: string
}

export class ResortFinder extends BaseComponent {
  static cssClass = "resort-finder"

  private suggestType!: string
  private filter!: Filter
  private results!: BElement
  private animeInstance?: anime.AnimeInstance
  private filterButton!: BElement
  private filterBlock!: BElement
  private countrySelect!: HTMLSelectElement
  private airportSelect!: HTMLSelectElement
  private airport?: string
  private week!: number
  private futureTabs!: Tabs
  private pastTabs!: Tabs
  private playButtons!: BElement[]

  init() {
    this.filterBlock = this.element("filter")!
    this.filter = new Filter(this.filterBlock.element, {
      onChange: () => this.reload(),
    })
    this.filterButton = this.element("filter-button")!
    this.filterButton.element.addEventListener("click", (e) => {
      e.stopPropagation()
      this.toggleFilter()
    })

    this.countrySelect = this.element("search-country")!.element as HTMLSelectElement
    this.countrySelect.addEventListener("change", () => {
      this.reload(true)
    })

    this.airportSelect = this.element("search-airport")!.element as HTMLSelectElement
    this.airportSelect.addEventListener("change", () => {
      this.airport = this.airportSelect.selectedIndex === 0 ? undefined : this.airportSelect.value
      this.reload()
    })

    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement
      const filterDropdown = this.element("filter-dropdown")?.element

      if (
        filterDropdown &&
        !filterDropdown.contains(target) &&
        this.filterBlock.hasModifier("is-shown")
      ) {
        this.closeFilter()
      }
    })

    this.results = this.element("results")!

    const [futureTabs, pastTabs] = this.elements("tabs").map(
      (tab) =>
        new Tabs(tab.element, {
          onChange: (key: string) => this.loadTab(key),
        }),
    )

    this.futureTabs = futureTabs
    this.pastTabs = pastTabs

    this.week = 0

    const when = this.element("search-when")!.element as HTMLSelectElement
    when.addEventListener("change", () => {
      const { value } = when

      if (value === "next3days") {
        this.week = 0
        this.futureTabs.setActiveTab("snow")
      } else if (value === "now") {
        this.week = 0
        this.futureTabs.setActiveTab("best")
      } else {
        this.week = Number(value)
      }

      const isHistory = this.week > 0
      this.futureTabs.toggleClass("is-hidden", isHistory)
      this.pastTabs.toggleClass("is-hidden", !isHistory)
      const tabs = isHistory ? this.pastTabs : this.futureTabs
      tabs.refresh()

      if (tabs.activeKey) this.loadTab(tabs.activeKey)
    })

    // Automatically load cards on initialisation
    const tabs = this.week === 0 ? futureTabs : pastTabs
    if (tabs.activeKey) this.changeType(tabs.activeKey)
    this.initPlayButtons()
  }

  private changeType(key: string) {
    this.suggestType = key
  }

  private loadTab(key: string) {
    this.changeType(key)
    this.reload(true)
  }

  private reload(resetAirports = false) {
    if (resetAirports) {
      this.airport = undefined
      this.airportSelect.options[0].selected = true
    }

    if (!this.suggestType || !this.filter) return

    const { pistes, lifts, checkboxes } = this.filter.getState()

    let url = `/resort-finder?type=${this.suggestType}`
    url += `&country=${this.countrySelect.value}`
    if (this.airport) url += `&airport=${this.airport}`
    url += `&week=${this.week}`
    if (pistes) url += `&pistes=${pistes}`
    if (lifts) url += `&lifts=${lifts}`
    Object.keys(checkboxes).forEach((key) => {
      if (checkboxes[key]) url += `&${key}=1`
    })
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        if (resetAirports) this.setAirports(json.airports)
        this.results.setHTML(json.results)
        this.initPlayButtons()
      })
  }

  private setAirports(airports: ResponseAirport[]) {
    const { options } = this.airportSelect

    for (let i = 0; i < airports.length; i++) {
      const airport = airports[i]
      // first option is "Any airport
      if (i + 1 < options.length) {
        options[i + 1].value = airport.code
        options[i + 1].textContent = airport.name
      } else {
        const option = document.createElement("option")
        option.value = airport.code
        option.textContent = airport.name
        this.airportSelect.appendChild(option)
      }
    }

    for (let i = options.length - 1; i > airports.length; i--) {
      options[i].remove()
    }
  }

  private toggleFilter() {
    const isShown = this.filterBlock.hasModifier("is-shown")

    this.filterBlock.toggleModifier("is-shown")

    this.filterButton.toggleModifier("active", !isShown)

    if (isMotionReduced()) {
      return
    }

    if (this.animeInstance?.completed === false) {
      this.animeInstance.reverse()
      this.animeInstance.play()
      return
    }

    this.filterBlock.toggleModifier("is-shown", true)
    const rect = this.filterBlock.element.getBoundingClientRect()

    let animeHeightProperty = [0, rect.height]
    let animeScaleYProperty = [0, 1]
    if (isShown) {
      animeHeightProperty = animeHeightProperty.reverse()
      animeScaleYProperty = animeScaleYProperty.reverse()
    }

    this.animeInstance = anime({
      targets: [this.filterBlock.element],
      easing: "easeInOutQuad",
      duration: 250,
      height: animeHeightProperty,
      scaleY: animeScaleYProperty,
      complete: () => {
        if (isShown) this.filterBlock.toggleModifier("is-shown")

        this.filterBlock.element.style.removeProperty("height")
        this.filterBlock.element.style.removeProperty("transform")
      },
    })
  }

  private closeFilter() {
    const isShown = this.filterBlock.hasModifier("is-shown")

    if (!isShown) return

    this.filterBlock.toggleModifier("is-shown", false)
    this.filterButton.toggleModifier("active", false)

    if (isMotionReduced()) {
      return
    }

    if (this.animeInstance?.completed === false) {
      this.animeInstance.reverse()
      this.animeInstance.play()
      return
    }

    const rect = this.filterBlock.element.getBoundingClientRect()
    this.animeInstance = anime({
      targets: [this.filterBlock.element],
      easing: "easeInOutQuad",
      duration: 250,
      height: [rect.height, 0],
      scaleY: [1, 0],
      complete: () => {
        this.filterBlock.toggleModifier("is-shown", false)
        this.filterBlock.element.style.removeProperty("height")
        this.filterBlock.element.style.removeProperty("transform")
      },
    })
  }

  initPlayButtons() {
    this.playButtons = this.elements("resort-card-play")
    if (!this.playButtons || this.playButtons.length === 0) {
      return
    }

    this.playButtons.forEach((button) => {
      button.element.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()

        const videoUrl = button.element.getAttribute("data-video-url")
        if (videoUrl) {
          openVideoModal(videoUrl)
        }
      })
    })
  }
}
